import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from "@sentry/angular";

import { loadSystemLanguage } from './app/i18n/system-language';
import { environment } from './environments/environment';
import { BUILD_INFO } from './environments/build-info';
import { filterBreadcrumb, scrubDeep, scrubLinkCodes } from './app/shared/link-code-scrub';
import { isParticipantView, isStaffHost, replayRates } from './app/shared/replay-policy';

// Which kind of page this is, worked out here rather than read from the loader in index.html.
// The loader sets the same two flags for its own third-party block, but it is an inline script:
// when its CSP hash went stale the browser dropped it, every page then looked like a creator
// page, and participant pages recorded replays until it was fixed (shared/replay-policy.ts).
const { hostname, pathname } = window.location;
// Participant views promise that nothing identifies the person: no session replay, no IP address,
// and no link code in any URL Sentry sees (docs/plans/PARTICIPANTS_REDESIGN_PLAN.md, WP13).
const participantView = isParticipantView(hostname, pathname) || !!(window as any).__durveyParticipantView;
// The admin dashboard's own site (index.html, docs/plans/CEO_HOST_PLAN.md).
const staffView = isStaffHost(hostname) || !!(window as any).__durveyStaffView;
// What may be recorded, and how thinly: participant and admin screens never, our own headless runs
// never, staging only when a session hit an error. Errors still go to Sentry from all of them.
const replay = replayRates({
  hostname, pathname,
  userAgent: navigator.userAgent,
  webdriver: navigator.webdriver,
  production: environment.production,
});

// Initialize Sentry as early as possible
if (environment.sentry.enabled && environment.sentry.dsn) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(replay.session || replay.onError ? [Sentry.replayIntegration({
        // Replay records every session (buffer mode when only the on-error rate
        // applies), so its recording cost is paid by everyone, not just the
        // sampled few. On DOM-heavy screens (statement-catalog projects: hundreds
        // of dimension chips) serializing mutation bursts froze the tab. Past this
        // many mutations in one batch, stop the replay instead of the page.
        mutationLimit: 1000,
      })] : []),
    ],
    // Performance Monitoring
    tracesSampleRate: environment.production ? 0.1 : 1.0, // Capture 100% in dev, 10% in prod
    // Session Replay (creator pages only; the rates come from shared/replay-policy.ts)
    replaysSessionSampleRate: replay.session,
    replaysOnErrorSampleRate: replay.onError,
    // IP addresses and other default PII only for creator pages
    sendDefaultPii: !participantView,
    // Link codes are logins; they leave the browser on no page.
    beforeSend: (event) => scrubDeep(event),
    beforeSendTransaction: (event) => scrubDeep(event),
    // Participant pages send no console breadcrumbs: the console can hold answers and comments.
    beforeBreadcrumb: (breadcrumb) => filterBreadcrumb(breadcrumb, participantView),
    environment: environment.production ? 'production' : 'staging',
    // The commit, not the product version (BUILD_INFO.version, from package.json): one version
    // spans many deploys, so with it Sentry could not tell two deploys apart — which is half of
    // why an issue list is hard to read. It is also the name source maps have
    // to be uploaded under, so the two must agree.
    release: `durvey@${BUILD_INFO.commit}`,
  });
  if (staffView) {
    Sentry.setTag('host', 'ceo');
  }
  if (participantView) {
    Sentry.setUser(null);
    Sentry.getCurrentScope().addEventProcessor((event) => {
      if (event.request?.url) { event.request.url = scrubLinkCodes(event.request.url); }
      delete event.user;
      return event;
    });
  }
}

// The interface's language loads before the app's code is imported, so every marked text — also one
// built when a module loads — comes out translated (i18n/system-language.ts). Participant pages read
// the survey's language through their own tables and skip it.
(participantView ? Promise.resolve('en') : loadSystemLanguage())
  .then(() => import('./app/app.module'))
  .then(({ AppModule }) => platformBrowserDynamic().bootstrapModule(AppModule))
  .catch(err => console.error(err));
